import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreHeading,
  OreInput,
  OreStack,
  OreToggleButton,
  SvgFilter,
} from '@edenredespana/oreneta'
import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { FilterModel, FilterResultModel } from '@/Shared/domain'
import { FiltersContainer, tableSearcherTranslations } from '@/Shared/ui/TableSearcher'

interface TableSearcherProps {
  handleSearch: (search: string, filter?: FilterResultModel[]) => void
  filters?: FilterModel[]
  searchPlaceholder?: string
  title?: string
}

export const TableSearcher = ({
  handleSearch,
  filters = [],
  searchPlaceholder,
  title,
}: TableSearcherProps): JSX.Element => {
  const { t } = useTranslation()
  const location = useLocation()

  const [search, setSearch] = useState<string>('')
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false)
  const [filtersValues, setFiltersValues] = useState<
    Array<FilterResultModel | undefined>
  >([])

  const isFilterResultModel = (
    item: FilterResultModel | undefined,
  ): item is FilterResultModel => {
    return !!item
  }

  const doSearch = (
    search: string,
    filters: Array<FilterResultModel | undefined>,
  ): void => {
    handleSearch(search, filters.filter(isFilterResultModel))
  }

  const handleEnterKey = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()

      doSearch(search, filtersValues)
    }
  }

  const searchButton = (): JSX.Element => (
    <OreButton
      category="primary"
      size="small"
      onClick={() => doSearch(search, filtersValues)}
    >
      {t(tableSearcherTranslations.search)}
    </OreButton>
  )

  const onSearch = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value)

    if (event.target.value === '') {
      doSearch('', filtersValues)
    }
  }

  useEffect(() => {
    setSearch('')
    const filterParams =
      location.search !== ''
        ? location.search
            .replace('?', '')
            .split('&')
            .map(param => {
              const [key, value] = param.split('=')
              return { name: key, value }
            })
        : undefined

    if (filters.length > 0 && filterParams !== undefined) {
      setFiltersValues(
        filters.map(filter => ({
          name: filter.id,
          value: filterParams?.find(param => param.name === filter.id)?.value || '',
        })),
      )
    }

    if (filterParams !== undefined && filterParams.length > 0) {
      setFiltersOpen(true)
      doSearch('', filterParams)
    }
  }, [location])

  return (
    <OreCardContainer radius="top">
      <OreCardWrapper space="large">
        <OreStack placeContent="stretch">
          {title ? <OreHeading size="headline-md">{title}</OreHeading> : null}
          <OreStack direction="row" placeContent="space-between" placeItems="center">
            <OreStack direction="row">
              <OreInput
                style={{ minWidth: '360px' }}
                type="search"
                name="search"
                id="search"
                onKeyDown={handleEnterKey}
                placeholder={
                  searchPlaceholder || t(tableSearcherTranslations.searchPlaceholder)
                }
                onChange={event => onSearch(event)}
                value={search}
              />
              {filtersOpen ? null : searchButton()}
            </OreStack>

            {filters.length > 0 ? (
              <OreToggleButton
                icon={<SvgFilter />}
                onClick={() => setFiltersOpen(!filtersOpen)}
                title={t(tableSearcherTranslations.filters)}
              >
                {t(tableSearcherTranslations.filters)}
              </OreToggleButton>
            ) : null}
          </OreStack>
          {filtersOpen ? (
            <OreStack placeContent="stretch">
              <FiltersContainer
                filters={filters}
                setFilters={setFiltersValues}
                currentFilters={filtersValues}
              />
              <OreStack direction="row">
                {searchButton()}
                <OreButton
                  category="secondary"
                  size="small"
                  onClick={() => {
                    setFiltersValues([])
                    setSearch('')
                    doSearch('', [])
                  }}
                >
                  {t(tableSearcherTranslations.clear)}
                </OreButton>
              </OreStack>
            </OreStack>
          ) : null}
        </OreStack>
      </OreCardWrapper>
    </OreCardContainer>
  )
}
